import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './cn';

/**
 * A block that is always there on a desktop and folded behind a button on a
 * phone.
 *
 * For the lower half of a long page: the recent activity under a dashboard,
 * the plant table under a summary. On a phone they are thousands of pixels
 * between a person and the bottom of the page, and they are not what the
 * page was opened for. Nothing is removed — it is one tap away.
 *
 * Put it INSIDE the section it folds, under that section's heading. From
 * `md` the body is `display: contents` and the button is not drawn, so the
 * children lay out as if this were not here.
 */
export function PhoneFold({ label, children, defaultOpen = false, forceOpen = false, className, bodyClassName }: {
  /** What is folded, as it reads after "Show" and "Hide": "recent activity". */
  label: string;
  children: ReactNode;
  defaultOpen?: boolean;
  /**
   * Opens it whatever the reader chose — for a form whose folded field has
   * an error. A message nobody can see is a Save button that does nothing.
   */
  forceOpen?: boolean;
  /** For the button's own spacing inside a card. */
  className?: string;
  /**
   * Phone-only classes for the opened body (`max-md:space-y-4`). From `md`
   * the body is `display: contents`; anything not `max-md:` would reach the
   * children on a desktop.
   */
  bodyClassName?: string;
}) {
  const [chosen, setOpen] = useState(defaultOpen);
  const open = chosen || forceOpen;
  const id = useId();
  return (
    <>
      <div className={cn('md:hidden', className)}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((o) => !o)}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-btn border border-neutral-300 bg-white px-3 text-small font-semibold text-neutral-800"
        >
          {open ? 'Hide' : 'Show'} {label}
          <ChevronDown aria-hidden className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')} />
        </button>
      </div>
      <div id={id} className={cn(open ? 'block' : 'hidden', bodyClassName, 'md:contents')}>{children}</div>
    </>
  );
}
